import { fetchJSON as fetchAPI } from './client';

export type ExecutiveDashboardSnapshot = {
  tenant_id: string; today_summary?: string; highest_priority?: string; highest_risk?: string;
  expected_revenue_opportunity?: string; expected_profit_opportunity?: string;
  urgent_inventory_decisions?: number; urgent_advertising_decisions?: number;
  recovery_opportunities?: string; recommendation_count: number; generated_at: string;
};

export type BusinessContextSnapshot = {
  tenant_id: string; dna_freshness?: string; engine_signals?: Record<string, string>;
  active_goals?: string[]; marketplace_events?: string[]; last_refreshed: string; complete: boolean;
};

export type OrchestratedRecommendation = {
  id: string; title: string; category: string; business_problem: string;
  root_cause?: string; evidence?: string[]; evidence_types?: string[];
  expected_revenue?: string; expected_profit?: string; expected_savings?: string;
  confidence: number; priority: number; priority_score: number;
  effort: string; risk: string; why_now?: string; why_not_alternatives?: string;
  status: string; action_label: string; source_engines?: string[]; dna_evidence: boolean;
};

export type BusinessDecision = {
  id: string; title: string; business_problem: string; root_cause?: string;
  expected_revenue?: string; expected_profit?: string; confidence: number;
  priority: number; status: string; source_engines?: string[];
};

export type MOBOpportunity = {
  id: string; title: string; type: string; expected_revenue?: string;
  expected_profit?: string; priority: number; confidence: number; time_sensitivity?: string;
};

export type ExecutiveReport = {
  id: string; type: string; title: string; period?: string;
  achievements?: string[]; risks?: string[]; top_opportunities?: string[];
  recommended_actions?: string[]; expected_impact?: string; generated_at: string;
};

export type ConversationResponse = {
  id: string; query: string; response: string; context?: string[];
  evidence?: string[]; confidence: number; suggested_actions?: string[]; timestamp: string;
};

export type EvidencePackage = {
  id: string; recommendation_id: string; evidence: string[]; evidence_types: string[];
  dna_references?: string[]; benchmarks?: string[]; confidence_drivers?: string[];
};

export type ReasoningExplanation = {
  id: string; recommendation_id: string; why: string; why_now: string;
  why_not?: string; what_changed?: string; assumptions?: string[]; risks?: string[];
};

export type LearningUpdate = {
  id: string; target: string; description: string; impact?: string; version?: number; updated_at: string;
};

export type KnowledgeNode = {
  id: string; type: string; label: string; relationships?: string[];
};

export type MOBTimelineEvent = {
  id: string; type: string; title: string; actor?: string; timestamp: string;
};

const COMMAND_BASE = import.meta.env.VITE_COMMAND_API ?? '/api/v1/command';
const fetchCmd = <T,>(path: string, init?: RequestInit) => fetchAPI<T>(`${COMMAND_BASE}${path}`, init);

export const marketplaceBrainApi = {
  dashboard: () => fetchCmd<ExecutiveDashboardSnapshot>('/marketplace-brain/dashboard'),
  context: () => fetchCmd<BusinessContextSnapshot>('/marketplace-brain/context'),
  decisions: () => fetchCmd<{ decisions: BusinessDecision[] }>('/marketplace-brain/decisions').then((r) => r.decisions),
  recommendations: () => fetchCmd<{ recommendations: OrchestratedRecommendation[] }>('/marketplace-brain/recommendations').then((r) => r.recommendations),
  opportunities: () => fetchCmd<{ opportunities: MOBOpportunity[] }>('/marketplace-brain/opportunities').then((r) => r.opportunities),
  priority: () => fetchCmd<{ priority: { recommendation_id: string; title: string; priority_score: number; rank: number }[] }>('/marketplace-brain/priority').then((r) => r.priority),
  evidence: (id: string) => fetchCmd<EvidencePackage>(`/marketplace-brain/evidence/${encodeURIComponent(id)}`),
  reasoning: (id: string) => fetchCmd<ReasoningExplanation>(`/marketplace-brain/reasoning/${encodeURIComponent(id)}`),
  report: (type: string) => fetchCmd<ExecutiveReport>(`/marketplace-brain/reports/${encodeURIComponent(type)}`),
  conversation: (query: string) =>
    fetchCmd<ConversationResponse>('/marketplace-brain/conversation', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query }),
    }),
  learning: () => fetchCmd<{ learning: LearningUpdate[] }>('/marketplace-brain/learning').then((r) => r.learning),
  knowledge: () => fetchCmd<{ knowledge: KnowledgeNode[] }>('/marketplace-brain/knowledge').then((r) => r.knowledge),
  history: () => fetchCmd<{ history: MOBTimelineEvent[] }>('/marketplace-brain/history').then((r) => r.history),
};
